import move from 'ext/lib/move';
import type { Action } from './collections.types';
import type { PatternMatcher } from './create-lifecycle';
import { createPatternCreator } from './create-lifecycle';
import { CHILD_MOVED } from './action-types';

/**
 * Move action payload structure
 */
export interface MovePayload {
  child: string;
  parent?: string;
  from: number;
  to: number;
  targetStepId?: string;
  action: 'before' | 'after';
}

/**
 * Parameters for creating a move action
 */
export interface MoveActionParams {
  parent?: string;
  from: number;
  to: number;
  targetStepId?: string;
}

/**
 * Move action creators for a specific entity type
 */
export interface MoveActions {
  /**
   * Create a move action for reordering children
   */
  move(child: string, params: MoveActionParams): Action<MovePayload>;

  /**
   * Pattern matchers for saga integration
   */
  patterns: {
    move: PatternMatcher;
  };
}

/**
 * Action representing child movement within a collection
 */
interface ChildMoveAction extends Action {
  type: typeof CHILD_MOVED;
  payload: MovePayload;
  meta: {
    type: string;
  };
}

/**
 * Create move action creators for a specific entity type
 *
 * @param entityType - Type of entity these actions apply to
 * @returns Object containing move action creator and pattern matchers
 */
export const createMoveActionsFor = (entityType: string): MoveActions => {
  const createPatternFor = createPatternCreator(entityType);

  return {
    /**
     * Create a move action for reordering children
     *
     * @param child - ID of child entity to move
     * @param params - Move parameters including indices and optional parent
     * @returns Move action
     */
    move: (
      child: string,
      { parent, from, to, targetStepId }: MoveActionParams
    ) => ({
      type: CHILD_MOVED,
      payload: {
        child,
        parent,
        from,
        to,
        targetStepId,
        action: to > from ? 'after' : 'before',
      },
      meta: { type: entityType },
    }),

    /**
     * Pattern matchers for saga integration
     */
    patterns: {
      move: createPatternFor(CHILD_MOVED),
    },
  };
};

/**
 * Type guard to check if action is a child move action
 */
const isChildMoveAction = (
  action: Action,
  entityType: string
): action is ChildMoveAction =>
  action.type === CHILD_MOVED && action.meta?.type === entityType;

/**
 * Higher-order reducer that handles reordering of child entities.
 * Works for both single entities and collections based on whether
 * the action includes a parent ID.
 *
 * @param field - Field name on entities that contains child ID arrays
 * @param entityType - Entity type this reducer handles
 * @returns Reducer function that handles child reordering
 */
export const moveChildren = <S extends Record<string, any>>(
  field: string,
  entityType: string
) => {
  return (state: S, action: Action): S => {
    if (isChildMoveAction(action, entityType)) {
      const { parent, from, to } = action.payload;

      if (parent) {
        // Handle collection case - update specific parent entity
        const entity = state[parent];

        if (entity && entity[field]) {
          return {
            ...state,
            [parent]: {
              ...entity,
              [field]: move(entity[field], from, to),
            },
          };
        }

        return state;
      }

      // Handle entity case - update root state field
      return state[field]
        ? { ...state, [field]: move(state[field], from, to) }
        : state;
    }

    return state;
  };
};
